import { Injectable } from '@angular/core';
import { UserHandler } from '@app-store/user/handler/user.handler';
import { ConfigManagerProperties } from '@shared/enums/common-data/config-manager-properties.enum';
import { RamRolesEnum, RamRolesKeyEnum, RamRolesTestEnum } from '@shared/enums/common-data/ram-roles.enum';
import { UserRamRolesModel, UserRolesModel } from '@shared/models/shared/user.model';
import { RegexRules } from '@shared/utils/regex/regex.utils';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { BehaviorSubject, interval } from 'rxjs';
import { filter, take, takeUntil, takeWhile, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserRolesService {

  userRamRoles: UserRamRolesModel = {};
  ramRolesKeyEnum = RamRolesKeyEnum;
  loggedUserRoles$ = new BehaviorSubject<UserRolesModel | undefined>(undefined);

  private isProd: boolean;
  private ramRolesEnum = RamRolesEnum;
  private ramRolesTestEnum = RamRolesTestEnum;

  constructor(
    private configManagerService: ConfigManagerService,
    private userHandler: UserHandler
  ) {
    this.runInitMethods();
  }

  getLoggedUserRolesValue(): UserRolesModel | undefined {
    return this.loggedUserRoles$.value;
  }

  checkIfAnyRoleMatch(keyRamRoles: RamRolesKeyEnum[], loggedUserRoles: UserRolesModel | undefined): boolean {
    if(this.isProdEmpty()) {
      this.runInitMethods();
    }

    const ramRolesToCheck = this.getRamRolesToCheck(keyRamRoles);
    return this.doesAnyRoleMatch(ramRolesToCheck, loggedUserRoles);
  }

  private runInitMethods(): void {
    interval(0).pipe(
      takeWhile(() => this.isProdEmpty()),
      tap(() => this.setIsProd()),
      filter(() => !this.isProdEmpty())
    ).subscribe(
      () => {
        this.setRamRolesToCheck();
        this.setRamRoles();
      }
    );
  }

  private setRamRoles(): void {
    this.userRamRoles = {
      [this.ramRolesKeyEnum.auditorRamRole]: this.isProd !== false ? [this.ramRolesEnum.auditorRamRole] : [this.ramRolesEnum.auditorRamRole, this.ramRolesTestEnum.auditorRamRole],
      [this.ramRolesKeyEnum.inspectorRamRole]: this.isProd !== false ? [this.ramRolesEnum.inspectorRamRole] : [this.ramRolesEnum.inspectorRamRole, this.ramRolesTestEnum.inspectorRamRole],
      [this.ramRolesKeyEnum.managerRamRole]: this.isProd !== false ? [this.ramRolesEnum.managerRamRole] : [this.ramRolesEnum.managerRamRole, this.ramRolesTestEnum.managerRamRole],
      [this.ramRolesKeyEnum.requester]: this.isProd !== false ? [this.ramRolesEnum.requester] : [this.ramRolesEnum.requester, this.ramRolesTestEnum.requester],
      [this.ramRolesKeyEnum.supervisorRamRole]: this.isProd !== false ? [this.ramRolesEnum.supervisorRamRole] : [this.ramRolesEnum.supervisorRamRole, this.ramRolesTestEnum.supervisorRamRole],
      [this.ramRolesKeyEnum.swoAuditorRamRole]: this.isProd !== false ? [this.ramRolesEnum.swoAuditorRamRole] : [this.ramRolesEnum.swoAuditorRamRole, this.ramRolesTestEnum.swoAuditorRamRole],
      [this.ramRolesKeyEnum.vicePresidentRamRole]: this.isProd !== false ? [this.ramRolesEnum.vicePresidentRamRole] : [this.ramRolesEnum.vicePresidentRamRole, this.ramRolesTestEnum.vicePresidentRamRole],
      [this.ramRolesKeyEnum.tabigatorRamRole]: this.isProd !== false ? [this.ramRolesEnum.tabigatorRamRole] : [this.ramRolesEnum.tabigatorRamRole, this.ramRolesTestEnum.tabigatorRamRole],
    };
  }

  private setRamRolesToCheck(): void {
    this.userHandler.getLoggedUser$.pipe(
      filter(user => !!user),
      take(1)
    ).subscribe(
      loggedUser => {
        const loggedUserRoles = loggedUser?.roles;
        if (!loggedUserRoles) return;

        let userRoles: UserRolesModel = {};
        Object.entries(loggedUserRoles).map(
          ([key, value]) => {
            key = key.replace(RegexRules.getTextBeforeSlash, '');
            value = value.replace(RegexRules.getTextBeforeSlash, '');    
            userRoles[key] = value;
          }
        );
        
        this.loggedUserRoles$.next(userRoles);
      }
    );
  }

  private getRamRolesToCheck(keyRamRoles: RamRolesKeyEnum[]): string[] {
    let rolesToCheck: string[] = [];
    keyRamRoles.forEach(role => {
      if (this.userRamRoles[role]) {
        rolesToCheck = [...rolesToCheck, ...this.userRamRoles[role]];
      }
    });

    return rolesToCheck;
  }

  private doesAnyRoleMatch(ramRolesToCheck: string[], loggedUserRoles: UserRolesModel | undefined): boolean {
    return loggedUserRoles ? ramRolesToCheck.some(ramRoles => loggedUserRoles[ramRoles]) : false;
  }

  private isProdEmpty(): boolean {
    return this.isProd == undefined;
  }

  private setIsProd(): void {
    this.isProd = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production)
  }
}

